import { isPurchasingTarget, type PurchasingTarget } from "./purchasingTarget";
import { scopedBrowserStorageKey, type StorageIdentity } from "./scopedBrowserStorage";
import type { ReferencePriceBasis } from "../api/articles";

// Active cart paste parser and read-only migration of owner-scoped legacy drafts.
// Current cart calculations and exports live in cartExport.ts and purchasingTarget.ts.

export type CartLine = {
  /** Artikelgruppe; zugleich stabile Zeilen-ID. */
  articleGroupKey: string;
  /** Artikelcode für die Anzeige (primärer Code oder Eingabe). */
  code: string;
  qty: number;
  /** Manuell gewählter Großhändler; null = automatisch aus Evidence. */
  supplierOverride: string | null;
};

export type ParsedCartInput = {
  code: string;
  qty: number;
};

// Toleranter Zeilen-Parser für Copy/Paste-Eingaben wie
// `123456 10`, `789012; 4; STK`, `345678` (Semikolon, Tab, Leerzeichen).
// Menge optional mit Standard 1; eine dritte Spalte (Einheit) wird ignoriert.
export function parsePastedCartLines(text: string): ParsedCartInput[] {
  const byCode = new Map<string, ParsedCartInput>();
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    let fields = line
      .split(/[;\t]/)
      .map((field) => field.trim())
      .filter(Boolean);
    if (fields.length === 1) {
      fields = fields[0].split(/\s+/);
    }
    const [code, qtyField] = fields;
    if (!code) continue;
    const qty = parseQtyField(qtyField);
    const dedupeKey = normalizeCartCode(code) || code;
    const existing = byCode.get(dedupeKey);
    if (existing) {
      existing.qty += qty;
    } else {
      byCode.set(dedupeKey, { code, qty });
    }
  }
  return [...byCode.values()];
}

function parseQtyField(field: string | undefined): number {
  if (!field) return 1;
  const value = Number(field.replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return 1;
  return value;
}

// Gleiche Normalisierung wie der Backend-Resolve (nur A-Z0-9, groß).
function normalizeCartCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

function clampDiscountPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

const STORAGE_KEY = "truucost.preisanfrage.v2";

export type StoredCartState = {
  lines: CartLine[];
  basis: ReferencePriceBasis;
  discountPct: number;
  target?: PurchasingTarget;
  targetMode?: "uninitialized" | "legacy" | "shared";
};

export const EMPTY_CART_STATE: StoredCartState = {
  targetMode: "uninitialized",
  lines: [],
  basis: "best_12m",
  discountPct: 0,
};

const VALID_BASES: ReferencePriceBasis[] = [
  "purchasing_last", "purchasing_best", "best_3m", "best_6m",
  "best_12m", "avg_12m", "avg_24m",
];

export class InvalidStoredCartError extends Error {}

export function loadCartState(identity: StorageIdentity | null = null, options: { throwOnError?: boolean } = {}): StoredCartState {
  const key = scopedBrowserStorageKey(STORAGE_KEY, identity);
  if (!key) return EMPTY_CART_STATE;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY_CART_STATE;
    let parsed: Partial<StoredCartState>;
    try {
      parsed = JSON.parse(raw) as Partial<StoredCartState>;
    } catch {
      throw new InvalidStoredCartError();
    }
    if (options.throwOnError && (!parsed || !Array.isArray(parsed.lines) || !parsed.lines.every(isValidStoredLine))) {
      throw new InvalidStoredCartError();
    }
    const basis = VALID_BASES.includes(parsed.basis as ReferencePriceBasis)
      ? (parsed.basis as ReferencePriceBasis)
      : "best_12m";
    const discountPct = clampDiscountPct(Number(parsed.discountPct ?? 0));
    const lines = Array.isArray(parsed.lines)
      ? parsed.lines.filter(isValidStoredLine)
      : [];
    return { lines, basis, discountPct, targetMode: isPurchasingTarget(parsed.target) ? "shared" : parsed.targetMode === "uninitialized" ? "uninitialized" : "legacy", ...(isPurchasingTarget(parsed.target) ? { target: parsed.target } : {}) };
  } catch (error) {
    if (options.throwOnError) throw error;
    return EMPTY_CART_STATE;
  }
}

function isValidStoredLine(value: unknown): value is CartLine {
  if (typeof value !== "object" || value === null) return false;
  const line = value as Partial<CartLine>;
  return (
    typeof line.articleGroupKey === "string" &&
    line.articleGroupKey.length > 0 &&
    typeof line.code === "string" &&
    typeof line.qty === "number" &&
    Number.isFinite(line.qty) &&
    line.qty > 0 &&
    (typeof line.supplierOverride === "string" ||
      line.supplierOverride === null)
  );
}
